import type { X402Endpoint } from "@/hooks/useX402Endpoints";
import { apexSlug } from "./providerSlug";

const MAX_SLUG_LEN = 60;
const FALLBACK_SLUG = "endpoint";

export function endpointSlug(name: string): string {
  const cleaned = (name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");

  if (!cleaned) return FALLBACK_SLUG;
  if (cleaned.length <= MAX_SLUG_LEN) return cleaned;

  const truncated = cleaned.slice(0, MAX_SLUG_LEN);
  const lastHyphen = truncated.lastIndexOf("-");
  if (lastHyphen > MAX_SLUG_LEN - 20) return truncated.slice(0, lastHyphen);
  return truncated;
}

/**
 * Extract a unique URL-safe short identifier from an endpoint id.
 *
 * x402 endpoint ids are formatted as `x402ep_<22-char-suffix>`. Taking the
 * first N chars produces collisions (every id shares the `x402ep_` prefix),
 * so we use the suffix after the last underscore. Falls back to the full id
 * for unprefixed formats.
 */
export function shortIdOf(endpointId: string): string {
  const lastUnderscore = endpointId.lastIndexOf("_");
  if (lastUnderscore >= 0 && lastUnderscore < endpointId.length - 1) {
    return endpointId.slice(lastUnderscore + 1);
  }
  return endpointId;
}

const SHORT_ID_SEPARATOR = "--";

export function buildEndpointPath(endpoint: X402Endpoint): string {
  const provider = apexSlug(endpoint.baseUrl);
  const slug = endpointSlug(endpoint.name);
  const short = shortIdOf(endpoint.id);
  return `/integrations/x402/${provider}/${slug}${SHORT_ID_SEPARATOR}${short}`;
}

/**
 * Reverse of buildEndpointPath: extract the short id from the trailing path
 * segment `<slug>--<short-id>`. Returns null if the segment doesn't contain
 * the `--` separator.
 */
export function extractShortIdFromPathSegment(segment: string): string | null {
  const idx = segment.lastIndexOf(SHORT_ID_SEPARATOR);
  if (idx < 0) return null;
  const candidate = segment.slice(idx + SHORT_ID_SEPARATOR.length);
  if (!candidate) return null;
  return candidate.toLowerCase();
}
